import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useAnchor } from '$contexts/anchor';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const ModuleAnchor = ({ title, slug, parent }) => {
    const ref = useRef(null);
    const { anchors, setCurrent, setAnchors } = useAnchor();

    useEffect(() => {
        // Register this anchor
        setAnchors(prevAnchors => {
            const exists = prevAnchors.some(a => a.slug === slug);
            if (!exists) {
                return [...prevAnchors, { slug, title }];
            }
            return prevAnchors;
        });

        return () => {
            // Unregister this anchor on unmount
            setAnchors(prevAnchors => prevAnchors.filter(a => a.slug !== slug));
        };
    }, [slug, title, setAnchors]);

    useEffect(() => {
        if (ref.current && typeof IntersectionObserver === 'function') {
            const handler = entries => {
                const entry = entries[0];

                if (entry.isIntersecting) {
                    // Element is entering viewport
                    setCurrent(entry?.target?.textContent);
                } else if (entry.boundingClientRect.top > 0) {
                    // Element is leaving viewport upward (scrolling up)
                    const currentIndex = anchors.findIndex(a => a.slug === slug);
                    if (currentIndex > 0) {
                        // Set to preceding anchor if it exists
                        setCurrent(anchors[currentIndex - 1].title);
                    }
                }
            };

            const observer = new IntersectionObserver(handler, {rootMargin: '0px 0px -80% 0px'});
            observer.observe(ref.current);

            return () => {
                observer.disconnect();
            };
        }
        return () => {};
    }, [ref.current, slug, anchors, setCurrent]);

    return (
        <div className={`bg-white relative ${parent === 'FAQ' ? 'pb-20 pt-30 md:pb-40 md:pt-70' : 'pt-20 md:pt-30'} `}>
            <div className='layout-container'>
                <div
                    className='module-anchor sm:w-4/6 md:w-7/12 md:ml-w-4/12 px-10 sm:px-5 md:px-10'
                    id={slug}
                    ref={ref}>
                    <p className='type-style-2 md:type-style-3'>
                        {title}
                    </p>
                </div>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

ModuleAnchor.propTypes = {
    slug: PropTypes.string,
    title: PropTypes.string,
    parent: PropTypes.string,
};

export default ModuleAnchor;
